import React, { useState, useEffect, useContext } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { AuthContext, API } from "../App";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { toast } from "sonner";
import { 
  Building2, RefreshCw, CheckCircle, AlertTriangle, TrendingUp, TrendingDown, 
  Wallet, ArrowUpRight, ArrowDownRight, ArrowLeftRight, HelpCircle, Clock,
  LayoutDashboard, Upload, List, ClipboardCheck, FileBarChart, CreditCard, Shield, LogOut, Menu, X,
  ArrowLeftRight as TransferIcon, Calendar, ChevronRight, AlertCircle
} from "lucide-react";

export default function WeeklySummaryPage() {
  const { user, token, logout } = useContext(AuthContext);
  const navigate = useNavigate();
  const [summary, setSummary] = useState(null);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [reconciling, setReconciling] = useState(false);
  const [selectedWeek, setSelectedWeek] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const headers = token ? { Authorization: `Bearer ${token}` } : {};

  useEffect(() => {
    fetchWeeklySummary();
    fetchHistory();
  }, []);

  const fetchWeeklySummary = async (weekEnding = null) => {
    setLoading(true);
    try {
      const params = weekEnding ? `?week_ending=${weekEnding}` : '';
      const response = await axios.get(`${API}/reconciliation/weekly${params}`, { headers, withCredentials: true });
      setSummary(response.data);
      setSelectedWeek(response.data.week_ending);
    } catch (error) {
      if (error.response?.status === 404) {
        setSummary(null);
      } else {
        toast.error("Failed to load weekly summary");
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchHistory = async () => {
    try {
      const response = await axios.get(`${API}/reconciliation/history?weeks=8`, { headers, withCredentials: true });
      setHistory(response.data);
    } catch (error) {
      console.error("Failed to load history:", error);
    }
  };

  const runReconciliation = async () => {
    setReconciling(true);
    try {
      await axios.post(`${API}/reconciliation/run`, { force_regenerate: true }, { headers, withCredentials: true });
      toast.success("Reconciliation completed");
      await fetchWeeklySummary();
      await fetchHistory();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Reconciliation failed");
    } finally {
      setReconciling(false);
    }
  };

  const finalizeSummary = async () => {
    if (!summary) return;
    try {
      await axios.post(`${API}/reconciliation/finalize/${summary.week_ending}`, {}, { headers, withCredentials: true });
      toast.success("Weekly summary finalized");
      await fetchWeeklySummary(summary.week_ending);
    } catch (error) {
      toast.error("Failed to finalize summary");
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-GB', { style: 'currency', currency: 'GBP' }).format(value || 0);
  };

  const formatDate = (dateStr) => {
    try {
      return new Date(dateStr).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
    } catch {
      return dateStr;
    }
  };

  const handleLogout = async () => {
    await logout();
    navigate("/");
  };

  const NavLink = ({ to, icon: Icon, children, active }) => (
    <Link to={to} className={`flex items-center gap-3 px-3 py-2 rounded-lg transition-colors ${active ? 'bg-zinc-800 text-white' : 'text-zinc-400 hover:text-white hover:bg-zinc-800/50'}`}>
      <Icon className="w-5 h-5" />
      <span className="text-sm font-medium">{children}</span>
    </Link>
  );

  return (
    <div className="min-h-screen bg-[#09090B]">
      {/* Sidebar */}
      <aside className="fixed left-0 top-0 bottom-0 w-64 bg-[#121214] border-r border-zinc-800 hidden lg:flex flex-col">
        <div className="p-6 border-b border-zinc-800">
          <Link to="/dashboard" className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-[#FCD34D] flex items-center justify-center">
              <Building2 className="w-6 h-6 text-[#1C1917]" />
            </div>
            <div>
              <h1 className="font-semibold text-white">BankFlow Control</h1>
              <p className="text-xs text-zinc-500">Financial Analysis</p>
            </div>
          </Link>
        </div>
        <nav className="flex-1 p-4 space-y-1">
          <NavLink to="/dashboard" icon={LayoutDashboard}>Dashboard</NavLink>
          <NavLink to="/upload" icon={Upload}>Upload Statements</NavLink>
          <NavLink to="/transactions" icon={List}>Transactions</NavLink>
          <NavLink to="/transfers" icon={TransferIcon}>Transfer Detection</NavLink>
          <NavLink to="/reconciliation" icon={Calendar} active>Weekly Summary</NavLink>
          <NavLink to="/review" icon={ClipboardCheck}>Review Queue</NavLink>
          <NavLink to="/reports" icon={FileBarChart}>Reports</NavLink>
          <NavLink to="/accounts" icon={CreditCard}>Bank Accounts</NavLink>
          {user?.role === 'admin' && <NavLink to="/admin" icon={Shield}>Admin Settings</NavLink>}
        </nav>
        <div className="p-4 border-t border-zinc-800">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center text-sm font-medium text-white">{user?.name?.charAt(0) || 'U'}</div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-white truncate">{user?.name}</p>
              <p className="text-xs text-zinc-500 truncate">{user?.email}</p>
            </div>
          </div>
          <Button variant="ghost" className="w-full justify-start text-zinc-400 hover:text-white hover:bg-zinc-800" onClick={handleLogout}>
            <LogOut className="w-4 h-4 mr-2" />Sign Out
          </Button>
        </div>
      </aside>

      {/* Mobile Header */}
      <header className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-[#121214] border-b border-zinc-800">
        <div className="flex items-center justify-between px-4 h-16">
          <Link to="/dashboard" className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-[#FCD34D] flex items-center justify-center">
              <Building2 className="w-5 h-5 text-[#1C1917]" />
            </div>
            <span className="font-semibold text-white">BankFlow</span>
          </Link>
          <Button variant="ghost" size="icon" className="text-zinc-400" onClick={() => setMobileMenuOpen(!mobileMenuOpen)}>
            {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </Button>
        </div>
      </header>

      {/* Main Content */}
      <main className="lg:ml-64 pt-16 lg:pt-0">
        <div className="p-4 md:p-8">
          {/* Header */}
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-white">Weekly Financial Summary</h1>
              <p className="text-zinc-400 text-sm mt-1">Master reconciliation across all bank accounts</p>
            </div>
            <div className="flex items-center gap-3">
              {history.length > 0 && (
                <Select value={selectedWeek || ''} onValueChange={(v) => fetchWeeklySummary(v)}>
                  <SelectTrigger className="w-48 bg-zinc-900/50 border-zinc-800 text-white">
                    <SelectValue placeholder="Select week" />
                  </SelectTrigger>
                  <SelectContent className="bg-[#121214] border-zinc-800">
                    {history.map(h => (
                      <SelectItem key={h.week_ending} value={h.week_ending} className="text-white hover:bg-zinc-800">
                        Week ending {formatDate(h.week_ending)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
              <Button
                onClick={runReconciliation}
                disabled={reconciling}
                className="bg-[#FCD34D] text-[#1C1917] hover:bg-[#FBBF24]"
                data-testid="run-reconciliation-btn"
              >
                <RefreshCw className={`w-4 h-4 mr-2 ${reconciling ? 'animate-spin' : ''}`} />
                {reconciling ? 'Running...' : 'Run Reconciliation'}
              </Button>
            </div>
          </div>

          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="w-8 h-8 border-2 border-[#FCD34D] border-t-transparent rounded-full animate-spin" />
            </div>
          ) : summary ? (
            <>
              {/* Summary Header Card */}
              <Card className="bg-gradient-to-r from-[#FCD34D]/10 to-transparent border-[#FCD34D]/20 mb-6">
                <CardContent className="p-6">
                  <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                    <div>
                      <p className="text-zinc-400 text-sm">Week Ending</p>
                      <h2 className="text-2xl font-bold text-white">{formatDate(summary.week_ending)}</h2>
                      <p className="text-zinc-500 text-xs mt-1">{formatDate(summary.week_start)} - {formatDate(summary.week_ending)}</p>
                    </div>
                    <div className="flex items-center gap-4">
                      <Badge 
                        variant="outline" 
                        className={`${
                          summary.status === 'finalized' 
                            ? 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20' 
                            : 'bg-amber-500/10 text-amber-400 border-amber-500/20'
                        }`}
                      >
                        {summary.status === 'finalized' ? (
                          <><CheckCircle className="w-3 h-3 mr-1" /> Finalized</>
                        ) : (
                          <><Clock className="w-3 h-3 mr-1" /> Draft</>
                        )}
                      </Badge>
                      {summary.status !== 'finalized' && user?.role === 'admin' && (
                        <Button variant="outline" size="sm" onClick={finalizeSummary} className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10">
                          <CheckCircle className="w-4 h-4 mr-2" />
                          Finalize
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Aggregate Position - Master Figure */}
              <Card className="bg-[#121214] border-zinc-800 mb-6" data-testid="aggregate-position-card">
                <CardHeader className="pb-2">
                  <CardTitle className="text-lg text-white flex items-center gap-2">
                    <Wallet className="w-5 h-5 text-[#FCD34D]" />
                    Aggregate Bank Position
                  </CardTitle>
                  <CardDescription className="text-zinc-500">
                    Master reconciled figure across all accounts
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="text-center py-6 border-b border-zinc-800 mb-6">
                    <p className="text-4xl md:text-5xl font-bold text-white mb-2">
                      {formatCurrency(summary.aggregate_closing_balance)}
                    </p>
                    <p className="text-zinc-500 text-sm">Total Aggregate Balance</p>
                  </div>
                  
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div className="p-4 bg-emerald-500/5 border border-emerald-500/20 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <ArrowUpRight className="w-4 h-4 text-emerald-500" />
                        <span className="text-xs text-zinc-400">Reconciled Income</span>
                      </div>
                      <p className="text-xl font-bold text-emerald-500">{formatCurrency(summary.total_reconciled_income)}</p>
                    </div>
                    
                    <div className="p-4 bg-red-500/5 border border-red-500/20 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <ArrowDownRight className="w-4 h-4 text-red-500" />
                        <span className="text-xs text-zinc-400">Reconciled Expenditure</span>
                      </div>
                      <p className="text-xl font-bold text-red-500">{formatCurrency(summary.total_reconciled_expenditure)}</p>
                    </div>
                    
                    <div className="p-4 bg-indigo-500/5 border border-indigo-500/20 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        <ArrowLeftRight className="w-4 h-4 text-indigo-400" />
                        <span className="text-xs text-zinc-400">Internal Transfers</span>
                      </div>
                      <p className="text-xl font-bold text-indigo-400">{formatCurrency(summary.total_internal_transfers)}</p>
                    </div>
                    
                    <div className="p-4 bg-zinc-800/50 border border-zinc-700 rounded-lg">
                      <div className="flex items-center gap-2 mb-2">
                        {summary.net_position_change >= 0 ? (
                          <TrendingUp className="w-4 h-4 text-emerald-500" />
                        ) : (
                          <TrendingDown className="w-4 h-4 text-red-500" />
                        )}
                        <span className="text-xs text-zinc-400">Net Change</span>
                      </div>
                      <p className={`text-xl font-bold ${summary.net_position_change >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                        {summary.net_position_change >= 0 ? '+' : ''}{formatCurrency(summary.net_position_change)}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Account-by-Account Summary */}
              <Card className="bg-[#121214] border-zinc-800 mb-6" data-testid="account-summaries-card">
                <CardHeader>
                  <CardTitle className="text-lg text-white">Account-by-Account Summary</CardTitle>
                  <CardDescription className="text-zinc-500">
                    {summary.accounts_reconciled} accounts reconciled • {summary.transactions_processed} transactions processed
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {summary.account_summaries.map((acc, index) => (
                      <div 
                        key={acc.account_id} 
                        className={`p-4 rounded-lg border ${acc.transaction_count > 0 ? 'bg-zinc-900/50 border-zinc-700' : 'bg-zinc-900/20 border-zinc-800'}`}
                      >
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 rounded-full bg-zinc-800 flex items-center justify-center text-sm font-medium text-[#FCD34D]">
                              {index + 1}
                            </div>
                            <div>
                              <p className="font-medium text-white">{acc.account_name}</p>
                              <p className="text-xs text-zinc-500">{acc.bank_name || 'Bank Account'}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-bold text-white">{formatCurrency(acc.closing_balance)}</p>
                            <p className="text-xs text-zinc-500">Closing Balance</p>
                          </div>
                        </div>
                        
                        {acc.transaction_count > 0 && (
                          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 pt-3 border-t border-zinc-800">
                            <div>
                              <p className="text-xs text-zinc-500">Opening</p>
                              <p className="text-sm font-mono text-zinc-300">{formatCurrency(acc.opening_balance)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-zinc-500">Income</p>
                              <p className="text-sm font-mono text-emerald-500">{formatCurrency(acc.total_incoming)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-zinc-500">Outgoing</p>
                              <p className="text-sm font-mono text-red-500">{formatCurrency(acc.total_outgoing)}</p>
                            </div>
                            <div>
                              <p className="text-xs text-zinc-500">Net Movement</p>
                              <p className={`text-sm font-mono ${acc.net_movement >= 0 ? 'text-emerald-500' : 'text-red-500'}`}>
                                {acc.net_movement >= 0 ? '+' : ''}{formatCurrency(acc.net_movement)}
                              </p>
                            </div>
                            <div>
                              <p className="text-xs text-zinc-500">Transactions</p>
                              <p className="text-sm font-mono text-zinc-300">{acc.transaction_count}</p>
                            </div>
                          </div>
                        )}
                        
                        {(acc.unmatched_receipts > 0 || acc.flagged_transactions > 0) && (
                          <div className="flex gap-2 mt-3">
                            {acc.unmatched_receipts > 0 && (
                              <Badge variant="outline" className="bg-amber-500/10 text-amber-400 border-amber-500/20 text-xs">
                                {acc.unmatched_receipts} unmatched
                              </Badge>
                            )}
                            {acc.flagged_transactions > 0 && (
                              <Badge variant="outline" className="bg-red-500/10 text-red-400 border-red-500/20 text-xs">
                                {acc.flagged_transactions} flagged
                              </Badge>
                            )}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Unallocated & Outstanding */}
              <div className="grid md:grid-cols-2 gap-6 mb-6">
                <Card className="bg-[#121214] border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-base text-white flex items-center gap-2">
                      <HelpCircle className="w-4 h-4 text-amber-500" />
                      Unallocated Receipts
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold text-amber-500">{formatCurrency(summary.unallocated_receipts)}</p>
                    <p className="text-zinc-500 text-sm">{summary.unallocated_receipts_count} receipt(s) need classification</p>
                    {summary.unallocated_receipts_count > 0 && (
                      <Link to="/review">
                        <Button variant="outline" size="sm" className="mt-3 border-zinc-700 text-zinc-300 hover:bg-zinc-800">
                          Review Now <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </Link>
                    )}
                  </CardContent>
                </Card>

                <Card className="bg-[#121214] border-zinc-800">
                  <CardHeader>
                    <CardTitle className="text-base text-white flex items-center gap-2">
                      <AlertCircle className="w-4 h-4 text-red-500" />
                      Outstanding Unidentified
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <p className="text-2xl font-bold text-red-500">{formatCurrency(summary.outstanding_unidentified)}</p>
                    <p className="text-zinc-500 text-sm">{summary.outstanding_unidentified_count} payment(s) need identification</p>
                    {summary.outstanding_unidentified_count > 0 && (
                      <Link to="/review">
                        <Button variant="outline" size="sm" className="mt-3 border-zinc-700 text-zinc-300 hover:bg-zinc-800">
                          Review Now <ChevronRight className="w-4 h-4 ml-1" />
                        </Button>
                      </Link>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Anomalies */}
              {summary.anomaly_count > 0 && (
                <Card className="bg-red-500/5 border-red-500/20 mb-6" data-testid="anomalies-card">
                  <CardHeader>
                    <CardTitle className="text-lg text-white flex items-center gap-2">
                      <AlertTriangle className="w-5 h-5 text-red-500" />
                      Anomalies Detected ({summary.anomaly_count})
                    </CardTitle>
                    <CardDescription className="text-red-400/70">
                      Issues requiring attention
                    </CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-3">
                      {summary.anomalies.map((anomaly, index) => (
                        <div key={anomaly.anomaly_id || index} className="p-4 bg-zinc-900/50 rounded-lg border border-zinc-800">
                          <div className="flex items-start justify-between mb-2">
                            <div className="flex items-center gap-2">
                              <Badge variant="outline" className={`text-xs ${
                                anomaly.severity === 'critical' ? 'bg-red-500/20 text-red-400 border-red-500/30' :
                                anomaly.severity === 'high' ? 'bg-orange-500/20 text-orange-400 border-orange-500/30' :
                                anomaly.severity === 'medium' ? 'bg-amber-500/20 text-amber-400 border-amber-500/30' :
                                'bg-zinc-500/20 text-zinc-400 border-zinc-500/30'
                              }`}>
                                {anomaly.severity}
                              </Badge>
                              <span className="text-xs text-zinc-500">{anomaly.anomaly_type.replace(/_/g, ' ')}</span>
                            </div>
                          </div>
                          <p className="text-white text-sm mb-2">{anomaly.description}</p>
                          <p className="text-zinc-500 text-xs">{anomaly.suggested_action}</p>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              )}

              {/* Summary Output */}
              <Card className="bg-[#121214] border-zinc-800">
                <CardHeader>
                  <CardTitle className="text-lg text-white">Weekly Summary Output</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="bg-zinc-900/50 rounded-lg p-6 font-mono text-sm">
                    <p className="text-[#FCD34D] font-bold mb-4">Week Ending {formatDate(summary.week_ending)}</p>
                    {summary.account_summaries.filter(a => a.closing_balance > 0).map(acc => (
                      <p key={acc.account_id} className="text-zinc-300 mb-1">
                        • {acc.account_name} Closing Balance: {formatCurrency(acc.closing_balance)}
                      </p>
                    ))}
                    <p className="text-white font-bold mt-4 pt-4 border-t border-zinc-800">
                      Aggregate Bank Position: {formatCurrency(summary.aggregate_closing_balance)}
                    </p>
                  </div>
                </CardContent>
              </Card>
            </>
          ) : (
            <Card className="bg-[#121214] border-zinc-800">
              <CardContent className="p-12 text-center">
                <Calendar className="w-12 h-12 text-zinc-700 mx-auto mb-4" />
                <p className="text-zinc-400 font-medium mb-2">No Weekly Summary Available</p>
                <p className="text-zinc-500 text-sm mb-4">Run reconciliation to generate your first weekly summary</p>
                <Button onClick={runReconciliation} disabled={reconciling} className="bg-[#FCD34D] text-[#1C1917] hover:bg-[#FBBF24]">
                  <RefreshCw className={`w-4 h-4 mr-2 ${reconciling ? 'animate-spin' : ''}`} />
                  Run Reconciliation
                </Button>
              </CardContent>
            </Card>
          )}
        </div>
      </main>
    </div>
  );
}
